import React, { useEffect, useState } from 'react';
import { onAuthStateChange, getCurrentUser } from './lib/auth.js';
import { acceptInvite } from './lib/invites.js';
import { LoginPage } from './pages/LoginPage.jsx';
import { AuthCallbackPage } from './pages/AuthCallbackPage.jsx';
import { AcceptInvitePage } from './pages/AcceptInvitePage.jsx';
import { MainApp } from './pages/MainApp.jsx';
import { LoadingScreen } from './components/LoadingScreen.jsx';
import { LandingPage } from './pages/marketing/LandingPage.jsx';
import { PrivacyPolicyPage } from './pages/marketing/PrivacyPolicyPage.jsx';
import { TermsPage } from './pages/marketing/TermsPage.jsx';
import { DataDeletionPage } from './pages/marketing/DataDeletionPage.jsx';

// Normalize a route: strip trailing slash (except for root "/")
function normalizeRoute(path) {
  if (!path || path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

export default function App() {
  const [user, setUser] = useState(undefined);
  const [route, setRoute] = useState(normalizeRoute(window.location.pathname));
  const [acceptingPending, setAcceptingPending] = useState(false);

  useEffect(() => {
    getCurrentUser().then(setUser);
    const unsubscribe = onAuthStateChange((u) => setUser(u));
    const onPop = () => setRoute(normalizeRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => {
      unsubscribe();
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  // After login: if there's a pending invite code, auto-accept it
  useEffect(() => {
    if (!user) return;
    const pendingCode = localStorage.getItem('tt_pending_invite');
    if (!pendingCode) return;

    setAcceptingPending(true);
    acceptInvite(pendingCode).then(result => {
      localStorage.removeItem('tt_pending_invite');
      if (result.success) {
        window.history.replaceState({}, '', '/app');
        setRoute('/app');
      } else {
        window.history.replaceState({}, '', `/invite/${pendingCode}`);
        setRoute(`/invite/${pendingCode}`);
      }
      setAcceptingPending(false);
    }).catch(() => {
      localStorage.removeItem('tt_pending_invite');
      setAcceptingPending(false);
    });
  }, [user]);

  // After login: redirect from public routes to /app (in useEffect — never during render)
  useEffect(() => {
    if (!user) return;
    if (route === '/' || route === '/login') {
      window.history.replaceState({}, '', '/app');
      setRoute('/app');
    }
  }, [user, route]);

  // Initial loading state
  if (user === undefined || acceptingPending) {
    return <LoadingScreen />;
  }

  // ============================================================
  // ROUTE RESOLUTION (pure function — no state mutations)
  // ============================================================

  // OAuth callback after Google/Facebook login
  if (route === '/auth/callback') {
    return <AuthCallbackPage />;
  }

  // Invite acceptance: /invite/{code}
  const inviteMatch = route.match(/^\/invite\/([a-z0-9]+)/i);
  if (inviteMatch) {
    return (
      <AcceptInvitePage
        user={user}
        inviteCode={inviteMatch[1]}
        onAccepted={() => {
          window.location.href = '/app';
        }}
      />
    );
  }

  // Privacy policy and terms (always public)
  if (route === '/privatlivspolitik') {
    return <PrivacyPolicyPage />;
  }
  if (route === '/handelsbetingelser') {
    return <TermsPage />;
  }
  if (route === '/data-deletion' || route === '/slet-data') {
    return <DataDeletionPage />;
  }

  // Login page — always show, but if logged in, the useEffect above will redirect to /app
  if (route === '/login') {
    if (user) return <LoadingScreen />; // brief moment before redirect
    return <LoginPage />;
  }

  // App routes — require auth
  if (route === '/app' || route.startsWith('/app/')) {
    if (!user) {
      return <LoginPage />;
    }
    return <MainApp user={user} />;
  }

  // Root: marketing for visitors, app redirect handled by useEffect above
  if (route === '/' || route === '') {
    if (user) return <LoadingScreen />; // brief moment before redirect to /app
    return <LandingPage />;
  }

  // 404 fallback: show landing page (could be replaced with a 404 page later)
  return <LandingPage />;
}
